import { z } from "zod";

export const LoginRequest = z.object({ email: z.string().email().max(254), password: z.string().min(1).max(1024) });
export type LoginRequest = z.infer<typeof LoginRequest>;

export const Address = z.object({ name: z.string().nullable(), email: z.string() });
export type Address = z.infer<typeof Address>;

export const Mailbox = z.object({
  id: z.number(),
  path: z.string(),
  name: z.string(),
  role: z.enum(["inbox", "sent", "drafts", "trash", "junk", "archive", "flagged", "other"]),
  total: z.number(),
  unread: z.number(),
});
export type Mailbox = z.infer<typeof Mailbox>;

export const ThreadSummary = z.object({
  key: z.string(),
  latestMessageId: z.number(),
  subject: z.string(),
  from: Address,
  participants: z.array(z.string()),
  snippet: z.string(),
  date: z.string(),
  count: z.number(),
  unread: z.boolean(),
  starred: z.boolean(),
  hasAttachment: z.boolean(),
});
export type ThreadSummary = z.infer<typeof ThreadSummary>;

export const ThreadPage = z.object({ items: z.array(ThreadSummary), total: z.number(), nextCursor: z.string().nullable() });
export type ThreadPage = z.infer<typeof ThreadPage>;

export const ThreadMessage = z.object({
  id: z.number(),
  mailboxId: z.number(),
  from: Address,
  to: z.array(Address),
  cc: z.array(Address),
  subject: z.string(),
  date: z.string(),
  snippet: z.string(),
  unread: z.boolean(),
  starred: z.boolean(),
  hasAttachment: z.boolean(),
});
export type ThreadMessage = z.infer<typeof ThreadMessage>;

export const ThreadDetail = z.object({ key: z.string(), subject: z.string(), muted: z.boolean().optional(), messages: z.array(ThreadMessage) });
export type ThreadDetail = z.infer<typeof ThreadDetail>;

export const MessageBody = z.object({
  html: z.string().nullable(),
  text: z.string().nullable(),
  blockedImages: z.number(),
  attachments: z.array(z.object({ index: z.number(), filename: z.string(), contentType: z.string(), size: z.number() })),
  calendar: z.boolean(),
  /** From List-Unsubscribe. oneClick means Workspace can unsubscribe for the reader (RFC 8058). */
  unsubscribe: z.object({ oneClick: z.boolean(), url: z.string().nullable(), mailto: z.string().nullable() }).nullable().optional(),
  draftId: z.number().nullable().optional(),
});
export type MessageBody = z.infer<typeof MessageBody>;

export const MessageAction = z.object({
  opId: z.string().max(64),
  messageIds: z.array(z.number().int().positive()).min(1).max(1000),
  action: z.enum(["read", "unread", "star", "unstar", "archive", "trash"]),
});
export type MessageAction = z.infer<typeof MessageAction>;
